import React, { useEffect, useRef, useState } from "react";
import MainLayout from "../../Layout/mainLayout";
import { Link, useNavigate, useParams } from "react-router-dom";
import "./editBuilder.css";
import { updateBuilderResale, viewBuilderResale } from "../../Redux/Actions/builderResaleAction";
import { useDispatch, useSelector } from "react-redux";
import {
  viewCityListPagination,
  viewCountryListPagination,
  viewStateListPagination,
} from "../../Redux/Actions/settingsActions";
import toast from "react-hot-toast";
import AddImage from "../../Global Functions/addImage";

const EditBuilderResale = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const logoInputRef = useRef(null);

  const { countryList } = useSelector((state) => state.country);
  const { states } = useSelector((state) => state.states);
  const { cities } = useSelector((state) => state.city);
  const { loading = false } = useSelector((state) => state.builderResale || {});

  const [activeSection, setActiveSection] = useState(0);
  const [errors, setErrors] = useState({});
  const [logoPreview, setLogoPreview] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [logoDragOver, setLogoDragOver] = useState(false);

  const [builderData, setBuilderData] = useState({
    brand_name: "",
    brand_logo: "",
    organization_name: "",
    street_address: "",
    city: "",
    state: "",
    country: "",
  });

  const [stateData, setStateData] = useState({ country: "" });
  const [cityData, setCityData] = useState({ state: "", country: "" });

  useEffect(() => {
    dispatch(viewCountryListPagination());
  }, [dispatch]);

  useEffect(() => {
    if (id) {
      const fetchBuilder = async () => {
        try {
          const payload = { builder_resale_id: id };
          const response = await dispatch(viewBuilderResale(payload));
          console.log("davsdj",response)
          if (response?.data) {
            const d = response.data;
            setBuilderData({
              brand_name: d.brand_name || "",
              brand_logo: d.brand_logo || "",
              organization_name: d.organization_name || "",
              street_address: d.street_address || "",
              city: d.city?._id || d.city || "",
              state: d.state?._id || d.state || "",
              country: d.country?._id || d.country || "",
            });
            if (d.brand_logo) {
              setLogoPreview(d.brand_logo);
            }
            if (d.country?._id || d.country) {
              const countryVal = d.country?._id || d.country;
              setStateData({ country: countryVal });
              setCityData((prev) => ({ ...prev, country: countryVal }));
            }
            if (d.state?._id || d.state) {
              const stateVal = d.state?._id || d.state;
              setCityData((prev) => ({ ...prev, state: stateVal }));
            }
          }
        } catch (error) {
          toast.error("Failed to load builder details");
        }
      };
      fetchBuilder();
    }
  }, [id, dispatch]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (stateData.country !== "") {
        dispatch(viewStateListPagination(stateData));
      }
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [stateData.country, dispatch, stateData]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (cityData.state !== "") {
        dispatch(viewCityListPagination(cityData));
      }
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [cityData, cityData.state, dispatch]);

  const clearError = (field) => {
    setErrors((prev) => {
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const handleEditChange = (event) => {
    const { name, value } = event.target;

    if (name === "country") {
      setStateData({ country: value });
      setCityData((prev) => ({ ...prev, country: value, state: "" }));
      setBuilderData((prev) => ({
        ...prev,
        state: "",
        city: "",
        country: value,
      }));
      clearError("country");
      return;
    }

    if (name === "state") {
      setCityData((prev) => ({ ...prev, state: value }));
      setBuilderData((prev) => ({
        ...prev,
        city: "",
        state: value,
      }));
      return;
    }

    setBuilderData((prev) => ({ ...prev, [name]: value }));
    clearError(name);
  };

  const processLogoFile = async (file) => {
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Please upload an image file");
      return;
    }

    const preview = URL.createObjectURL(file);
    setLogoPreview(preview);

    try {
      setUploadingLogo(true);
      const url = await AddImage(file);
      setBuilderData((prev) => ({ ...prev, brand_logo: url }));
      clearError("brand_logo");
    } catch (error) {
      toast.error("Brand logo upload failed");
      setLogoPreview(null);
      setBuilderData((prev) => ({ ...prev, brand_logo: "" }));
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleLogoFileChange = (e) => {
    processLogoFile(e.target.files?.[0]);
  };

  const handleLogoDrop = (e) => {
    e.preventDefault();
    setLogoDragOver(false);
    processLogoFile(e.dataTransfer.files?.[0]);
  };

  const removeLogo = () => {
    setLogoPreview(null);
    setBuilderData((prev) => ({ ...prev, brand_logo: "" }));
    if (logoInputRef.current) logoInputRef.current.value = "";
  };

  const sections = ["Basic Info", "Address"];

  const handleNextSection = () => {
    if (activeSection === 0) {
      const sectionErrors = {};
      if (!builderData.brand_name.trim()) {
        sectionErrors.brand_name = "Builder name is required";
      }
      if (Object.keys(sectionErrors).length) {
        setErrors((prev) => ({ ...prev, ...sectionErrors }));
        return;
      }
    }
    const next = Math.min(sections.length - 1, activeSection + 1);
    setActiveSection(next);
  };

  const validate = () => {
    const newErrors = {};
    if (!builderData.brand_name.trim()) {
      newErrors.brand_name = "Builder name is required";
    }
    if (!builderData.country) {
      newErrors.country = "Country is required";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validate()) {
      if (!builderData.brand_name.trim()) {
        setActiveSection(0);
      } else if (!builderData.country) {
        setActiveSection(1);
      }
      return;
    }

    try {
      const payload = {
        brand_name: builderData.brand_name,
        brand_logo: builderData.brand_logo,
        organization_name: builderData.organization_name,
        street_address: builderData.street_address,
        country: builderData.country,
        builder_type: "resale",
      };

      if (builderData.state) payload.state = builderData.state;
      if (builderData.city) payload.city = builderData.city;

      const response = await dispatch(updateBuilderResale(id, payload));

      if (response?.statusCode === 200) {
        // toast.success("Builder resale updated successfully!");
        navigate("/builder_resale");
      }
    } catch (error) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to update builder resale";

      if (
        errorMessage.toLowerCase().includes("duplicate") ||
        errorMessage.toLowerCase().includes("already")
      ) {
        setErrors((prev) => ({ ...prev, brand_name: "Brand name already exists" }));
        setActiveSection(0);
        return toast.error("Brand name already exists");
      }

      toast.error(errorMessage);
    }
  };

  return (
    <MainLayout>
      <div className="ebr-wrapper">
        <div className="ebr-header">
          <div className="ebr-header-left">
            <div className="ebr-header-icon">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
                <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
              </svg>
            </div>
            <div>
              <h1 className="ebr-title">Edit Builder Resale</h1>
              <p className="ebr-subtitle">Update the details of this resale builder</p>
            </div>
          </div>

          <Link to="/builder_resale" className="ebr-view-btn">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
              <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
            </svg>
            View Builders
          </Link>
        </div>

        <div className="ebr-nav">
          {sections.map((sec, i) => (
            <button
              key={i}
              className={`ebr-nav-pill ${activeSection === i ? "ebr-nav-pill--active" : ""}`}
              onClick={() => setActiveSection(i)}
              type="button"
              disabled={loading || uploadingLogo}
            >
              <span className="ebr-nav-num">{String(i + 1).padStart(2, "0")}</span>
              {sec}
            </button>
          ))}
        </div>

        <form className="ebr-form" onSubmit={handleSubmit}>
          {/* ── Section 1: Basic Info ── */}
          <div className={`ebr-section ${activeSection === 0 ? "ebr-section--visible" : ""}`}>
            <div className="ebr-section-header">
              <span className="ebr-section-badge">01</span>
              <h2 className="ebr-section-title">Basic Information</h2>
            </div>

            <div className="ebr-grid ebr-grid-2">
              <div className="ebr-field">
                <label className="ebr-label">
                  Builder Name <span className="ebr-req">*</span>
                </label>
                <input
                  className={`ebr-input ${errors.brand_name ? "ebr-input--error" : ""}`}
                  type="text"
                  name="brand_name"
                  value={builderData.brand_name}
                  onChange={handleEditChange}
                  placeholder="e.g. John Doe"
                  disabled={loading}
                />
                {errors.brand_name && <span className="ebr-error-msg">{errors.brand_name}</span>}
              </div>

              <div className="ebr-field">
                <label className="ebr-label">
                  Organization Name <span className="ebr-optional">(Optional)</span>
                </label>
                <input
                  className="ebr-input"
                  type="text"
                  name="organization_name"
                  value={builderData.organization_name}
                  onChange={handleEditChange}
                  placeholder="e.g. Sunrise Real Estate Pvt Ltd"
                  disabled={loading}
                />
              </div>

              <div className="ebr-field ebr-field--full">
                <label className="ebr-label">
                  Brand Logo <span className="ebr-optional">(Optional)</span>
                </label>

                {logoPreview ? (
                  <div className="prv-icon-preview">
                    <div className="prv-icon-preview-img-wrap">
                      {uploadingLogo && (
                        <div className="prv-icon-uploading-overlay">
                          <svg className="prv-spinner" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
                          </svg>
                          <span>Uploading...</span>
                        </div>
                      )}
                      <img src={logoPreview} alt="Brand logo preview" className="prv-icon-preview-img" />
                    </div>

                    <div className="prv-icon-preview-info">
                      <span className="prv-icon-preview-label">Brand logo uploaded</span>
                      {builderData.brand_logo && (
                        <span className="prv-icon-preview-url">{builderData.brand_logo.slice(0, 48)}...</span>
                      )}

                      <div className="prv-icon-preview-actions">
                        <button
                          type="button"
                          className="prv-icon-change-btn"
                          onClick={() => logoInputRef.current?.click()}
                          disabled={uploadingLogo || loading}
                        >
                          Change
                        </button>
                        <button
                          type="button"
                          className="prv-icon-remove-btn"
                          onClick={removeLogo}
                          disabled={uploadingLogo || loading}
                        >
                          Remove
                        </button>
                      </div>
                    </div>

                    <input
                      ref={logoInputRef}
                      type="file"
                      accept="image/*"
                      style={{ display: "none" }}
                      onChange={handleLogoFileChange}
                    />
                  </div>
                ) : (
                  <div
                    className={`prv-icon-dropzone ${logoDragOver ? "prv-icon-dropzone--over" : ""}`}
                    onClick={() => logoInputRef.current?.click()}
                    onDragOver={(e) => { e.preventDefault(); setLogoDragOver(true); }}
                    onDragLeave={() => setLogoDragOver(false)}
                    onDrop={handleLogoDrop}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => e.key === "Enter" && logoInputRef.current?.click()}
                  >
                    <div className="prv-icon-dropzone-icon">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                        <circle cx="8.5" cy="8.5" r="1.5" />
                        <polyline points="21 15 16 10 5 21" />
                      </svg>
                    </div>

                    <div className="prv-icon-dropzone-text">
                      <span className="prv-icon-dropzone-title">
                        {logoDragOver ? "Drop image here" : "Upload brand logo"}
                      </span>
                      <span className="prv-icon-dropzone-sub">
                        Click to browse or drag & drop · PNG, JPG, SVG, WebP
                      </span>
                    </div>

                    <input
                      ref={logoInputRef}
                      type="file"
                      accept="image/*"
                      style={{ display: "none" }}
                      onChange={handleLogoFileChange}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* ── Section 2: Address ── */}
          <div className={`ebr-section ${activeSection === 1 ? "ebr-section--visible" : ""}`}>
            <div className="ebr-section-header">
              <span className="ebr-section-badge">02</span>
              <h2 className="ebr-section-title">Address Details</h2>
            </div>

            <div className="ebr-grid ebr-grid-2">
              <div className="ebr-field ebr-field--full">
                <label className="ebr-label">
                  Street Address <span className="ebr-optional">(Optional)</span>
                </label>
                <input
                  className="ebr-input"
                  type="text"
                  name="street_address"
                  value={builderData.street_address}
                  onChange={handleEditChange}
                  placeholder="Street, building, apartment..."
                  disabled={loading}
                />
              </div>

              <div className="ebr-field">
                <label className="ebr-label">
                  Country <span className="ebr-req">*</span>
                </label>
                <select
                  className={`ebr-select ${errors.country ? "ebr-input--error" : ""}`}
                  name="country"
                  value={builderData.country}
                  onChange={handleEditChange}
                  disabled={loading}
                >
                  <option value="">Select country</option>
                  {countryList?.data?.map((country) => (
                    <option key={country._id} value={country._id}>
                      {country.country_name}
                    </option>
                  ))}
                </select>
                {errors.country && <span className="ebr-error-msg">{errors.country}</span>}
              </div>

              <div className="ebr-field">
                <label className="ebr-label">
                  State <span className="ebr-optional">(Optional)</span>
                </label>
                <select
                  className={`ebr-select ${!builderData.country ? "ebr-select--disabled" : ""}`}
                  name="state"
                  value={builderData.state}
                  onChange={handleEditChange}
                  disabled={!builderData.country || loading}
                >
                  <option value="">
                    {!builderData.country ? "Select country first" : "Select state"}
                  </option>
                  {states?.data?.map((state) => (
                    <option key={state._id} value={state._id}>
                      {state.state_name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="ebr-field">
                <label className="ebr-label">
                  City <span className="ebr-optional">(Optional)</span>
                </label>
                <select
                  className={`ebr-select ${!builderData.state ? "ebr-select--disabled" : ""}`}
                  name="city"
                  value={builderData.city}
                  onChange={handleEditChange}
                  disabled={!builderData.state || loading}
                >
                  <option value="">
                    {!builderData.state ? "Select state first" : "Select city"}
                  </option>
                  {cities?.data?.map((city) => (
                    <option key={city._id} value={city._id}>
                      {city.city_name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* ── Footer ── */}
          <div className="ebr-footer">
            <div className="ebr-footer-nav">
              <button
                type="button"
                className="ebr-btn ebr-btn--ghost"
                onClick={() => setActiveSection((s) => Math.max(0, s - 1))}
                disabled={activeSection === 0 || loading || uploadingLogo}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="15 18 9 12 15 6" />
                </svg>
                Back
              </button>

              {activeSection < sections.length - 1 ? (
                <button
                  type="button"
                  className="ebr-btn ebr-btn--primary"
                  onClick={handleNextSection}
                  disabled={loading || uploadingLogo}
                >
                  Next
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="9 18 15 12 9 6" />
                  </svg>
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleSubmit}
                  className="ebr-btn ebr-btn--submit"
                  disabled={loading || uploadingLogo}
                >
                  {loading ? "Saving..." : (
                    <>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                      Update Builder Resale
                    </>
                  )}
                </button>
              )}
            </div>

            <Link to="/builder_resale" className="ebr-btn ebr-btn--cancel">
              Cancel
            </Link>
          </div>
        </form>
      </div>
    </MainLayout>
  );
};

export default EditBuilderResale;